import { ApiResponse, axiosInstance } from '@/lib/axios';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/react-query';
import { TSpace } from '../types/space.type';

export type TGetSingleSpaceResponse = ApiResponse<TSpace>;
export type TGetSingleSpaceParams = {
  spaceId: string;
};
export const getSingleSpace = async (params: TGetSingleSpaceParams): Promise<TGetSingleSpaceResponse> => {
  const url = `/space/${params.spaceId}`;

  return axiosInstance.get(url);
};

export const GET_SINGLE_SPACE_QUERY_KEY = 'get-single-space';

export const getSingleSpaceQueryOptions = <TData = TGetSingleSpaceResponse>(
  params: TGetSingleSpaceParams,
  select?: (data: TGetSingleSpaceResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_SINGLE_SPACE_QUERY_KEY,
      {
        spaceId: params?.spaceId,
      },
    ],
    queryFn: () => getSingleSpace(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useSingleSpaceQuery<TData>(options: {
  params: TGetSingleSpaceParams;
  queryConfig?: QueryConfig<typeof getSingleSpaceQueryOptions<TData>>;
  select: (data: TGetSingleSpaceResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useSingleSpaceQuery(options: {
  params: TGetSingleSpaceParams;
  queryConfig?: QueryConfig<typeof getSingleSpaceQueryOptions<TGetSingleSpaceResponse>>;
}): ReturnType<typeof useQuery<TGetSingleSpaceResponse>>;

// Implementation
export function useSingleSpaceQuery<TData = TGetSingleSpaceResponse>({
  queryConfig,
  params,
  select,
}: {
  params: TGetSingleSpaceParams;
  queryConfig?: QueryConfig<typeof getSingleSpaceQueryOptions<TData | TGetSingleSpaceResponse>>;
  select?: (data: TGetSingleSpaceResponse) => TData;
}) {
  return useQuery({
    ...getSingleSpaceQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<typeof useQuery<TData extends TGetSingleSpaceResponse ? TGetSingleSpaceResponse : TData>>;
}
